import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const GreenOpsPortal = ({ userTier, getFirebaseToken, backendUrl, onTriggerPricing }) => {
  const { t } = useTranslation();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // Form states
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('GCP');
  const [env, setEnv] = useState('Dev');
  const [registering, setRegistering] = useState(false);

  // Active/selected cluster for key management
  const [selectedCluster, setSelectedCluster] = useState(null);
  
  // API Key generation states
  const [keyName, setKeyName] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [quickGeneratingKey, setQuickGeneratingKey] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null); // stores raw key once
  const [rawKeysByCluster, setRawKeysByCluster] = useState({}); // in-memory map { [clusterId]: [{ rawKey, name, createdAt }] }
  const [activeRawKey, setActiveRawKey] = useState(null);
  const [mostRecentKeyToken, setMostRecentKeyToken] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState(null);

  // Clipboard copy feedback
  const [copiedHelm, setCopiedHelm] = useState(false);

  const isTierAuthorized = userTier && ['scaleup', 'enterprise'].includes(userTier.toLowerCase());
  const hasActiveApiKey = !!(
    activeRawKey ||
    newlyGeneratedKey ||
    mostRecentKeyToken ||
    (rawKeysByCluster[selectedCluster?.id] && rawKeysByCluster[selectedCluster.id].length > 0) ||
    (selectedCluster?.api_keys && selectedCluster.api_keys.length > 0)
  );

  const isClusterActive = !!(
    selectedCluster && (
      selectedCluster.status === 'ACTIVE' ||
      selectedCluster.status === 'HEALTHY' ||
      (selectedCluster.last_telemetry_at && selectedCluster.status !== 'OFFLINE' && selectedCluster.status !== 'DEGRADED')
    )
  );

  const fetchClusters = useCallback(async () => {
    if (!isTierAuthorized) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setErrorMsg(null);
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const response = await axios.get(`${backendUrl}/api/finops/clusters`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setClusters(response.data || []);
      if (response.data && response.data.length > 0) {
        // Keep selection or default to first
        setSelectedCluster(prev => {
          if (prev) {
            const updated = response.data.find(c => c.id === prev.id);
            return updated || response.data[0];
          }
          return response.data[0];
        });
      }
    } catch (err) {
      console.error('Error fetching clusters:', err);
      setErrorMsg(err.response?.data?.error || t('finops.portal.error_fetch_clusters', 'Error fetching clusters.'));
    } finally {
      setLoading(false);
    }
  }, [isTierAuthorized, getFirebaseToken, backendUrl, t]);

  const fetchClustersSilently = useCallback(async () => {
    if (!isTierAuthorized) return;
    try {
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const response = await axios.get(`${backendUrl}/api/finops/clusters`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = response.data || [];
      setClusters(data);
      setSelectedCluster(prev => {
        if (!prev) return data[0] || null;
        const updated = data.find(c => c.id === prev.id);
        return updated || prev;
      });
    } catch (err) {
      console.warn('Silent cluster poll error:', err);
    }
  }, [isTierAuthorized, getFirebaseToken, backendUrl]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  // Real-time telemetry handshake polling
  useEffect(() => {
    if (!selectedCluster) return;
    const isWaitingTelemetry = !selectedCluster.status || ['CONNECTING', 'PENDING'].includes(selectedCluster.status);
    if (!isWaitingTelemetry) return;

    const intervalId = setInterval(() => {
      fetchClustersSilently();
    }, 3500);

    return () => clearInterval(intervalId);
  }, [selectedCluster?.id, selectedCluster?.status, fetchClustersSilently]);

  // Sync active raw key when cluster selection changes
  useEffect(() => {
    if (selectedCluster?.id) {
      const clusterKeys = rawKeysByCluster[selectedCluster.id];
      if (clusterKeys && clusterKeys.length > 0) {
        setActiveRawKey(clusterKeys[0].rawKey);
      } else if (mostRecentKeyToken) {
        setActiveRawKey(mostRecentKeyToken);
      } else {
        setActiveRawKey(null);
      }
    } else {
      setActiveRawKey(null);
    }
  }, [selectedCluster?.id, rawKeysByCluster, mostRecentKeyToken]);

  const handleRegisterCluster = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      setRegistering(true);
      setErrorMsg(null);
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const response = await axios.post(
        `${backendUrl}/api/finops/clusters`,
        { name: name.trim(), cloud_provider: provider, environment: env },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      setName('');
      await fetchClusters();
      // Auto-select the newly registered cluster
      if (response.data && response.data.id) {
        const newC = response.data;
        newC.api_keys = [];
        setSelectedCluster(newC);
      }
    } catch (err) {
      console.error('Error registering cluster:', err);
      setErrorMsg(err.response?.data?.error || t('finops.portal.error_register', 'Error registering cluster.'));
    } finally {
      setRegistering(false);
    }
  };

  const handleGenerateKey = async (e) => {
    e.preventDefault();
    if (!selectedCluster) return;

    try {
      setGeneratingKey(true);
      setErrorMsg(null);
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const response = await axios.post(
        `${backendUrl}/api/finops/clusters/${selectedCluster.id}/keys`,
        { name: keyName.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const rawKey = response.data.raw_key;
      const keyEntry = { rawKey, name: keyName.trim() || 'API Key', createdAt: new Date() };

      setKeyName('');
      setNewlyGeneratedKey(rawKey);
      setActiveRawKey(rawKey);
      setMostRecentKeyToken(rawKey);
      setRawKeysByCluster(prev => ({
        ...prev,
        [selectedCluster.id]: [keyEntry, ...(prev[selectedCluster.id] || [])]
      }));
      setShowKeyModal(true);
      await fetchClusters();
    } catch (err) {
      console.error('Error generating key:', err);
      setErrorMsg(err.response?.data?.error || t('finops.portal.error_generate_key', 'Error generating API key.'));
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleQuickGenerateKeyAndPair = async () => {
    if (!selectedCluster) return;
    try {
      setQuickGeneratingKey(true);
      setErrorMsg(null);
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const autoKeyName = `Agent Key (${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
      const response = await axios.post(
        `${backendUrl}/api/finops/clusters/${selectedCluster.id}/keys`,
        { name: autoKeyName },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const rawKey = response.data.raw_key;
      const keyEntry = { rawKey, name: autoKeyName, createdAt: new Date() };

      setRawKeysByCluster(prev => ({
        ...prev,
        [selectedCluster.id]: [keyEntry, ...(prev[selectedCluster.id] || [])]
      }));
      setActiveRawKey(rawKey);
      setMostRecentKeyToken(rawKey);
      setNewlyGeneratedKey(rawKey);

      const cmd = `helm upgrade --install greenops-agent oci://us-central1-docker.pkg.dev/my-payments-agenda-2bdf0/greenops-charts/greenops-agent --namespace greenops-system --create-namespace --set config.apiKey="${rawKey}" --set config.clusterId="${selectedCluster.id}"`;
      copyToClipboard(cmd, setCopiedHelm);
      await fetchClustersSilently();
    } catch (err) {
      console.error('Error quick generating key:', err);
      setErrorMsg(err.response?.data?.error || t('finops.portal.error_generate_key', 'Error generating API key.'));
    } finally {
      setQuickGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm(t('finops.portal.confirm_revoke', 'Are you sure you want to revoke this API key?'))) return;

    try {
      setRevokingKeyId(keyId);
      setErrorMsg(null);
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      await axios.delete(`${backendUrl}/api/finops/keys/${keyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      await fetchClusters();
    } catch (err) {
      console.error('Error revoking key:', err);
      setErrorMsg(err.response?.data?.error || t('finops.portal.error_revoke_key', 'Error revoking API key.'));
    } finally {
      setRevokingKeyId(null);
    }
  };

  const copyToClipboard = (text, setCopied) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(err => console.warn('Clipboard write error:', err));
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Paywall View for Basic/Developer Tiers
  if (!isTierAuthorized) {
    return (
      <div className="glass-panel" style={{
        padding: '50px 30px',
        textAlign: 'center',
        background: 'rgba(15, 23, 42, 0.45)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        marginTop: '20px',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🔒</div>
        <h3 style={{ margin: '0 0 12px 0', color: '#F8FAFC', fontSize: '1.6rem', fontWeight: 'bold' }}>
          {t('finops.portal.paywall_title')}
        </h3>
        <p style={{ margin: '0 auto 25px auto', color: '#94A3B8', fontSize: '1.05rem', maxW: '600px', lineHeight: '1.6', maxWidth: '600px' }}>
          {t('finops.portal.paywall_subtitle')}
        </p>
        <button onClick={onTriggerPricing} className="btn-primary" style={{
          background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
          border: 'none',
          padding: '12px 28px',
          fontSize: '1rem',
          fontWeight: 'bold',
          color: '#FFF',
          borderRadius: '10px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
          transition: 'transform 0.2s'
        }}>
          {t('finops.portal.paywall_btn')}
        </button>
      </div>
    );
  }

  // Helm Command Formatting
  const getHelmCommand = (overrideKey) => {
    const keyString = overrideKey || activeRawKey || newlyGeneratedKey || mostRecentKeyToken || 'YOUR_INGESTION_KEY';
    const clusterString = selectedCluster ? selectedCluster.id : '<CLUSTER_ID>';
    return `helm upgrade --install greenops-agent oci://us-central1-docker.pkg.dev/my-payments-agenda-2bdf0/greenops-charts/greenops-agent --namespace greenops-system --create-namespace --set config.apiKey="${keyString}" --set config.clusterId="${clusterString}"`;
  };

  const handleCopyHelmSnippet = () => {
    if (!hasActiveApiKey) return;
    if (!activeRawKey && !newlyGeneratedKey && !mostRecentKeyToken) {
      handleQuickGenerateKeyAndPair();
    } else {
      copyToClipboard(getHelmCommand(), setCopiedHelm);
    }
  };

  return (
    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
      <style>{`
        @keyframes subtlePulse {
          0% { transform: scale(0.9); opacity: 0.7; box-shadow: 0 0 0 0 rgba(129, 140, 248, 0.7); }
          70% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 0 6px rgba(129, 140, 248, 0); }
          100% { transform: scale(0.9); opacity: 0.7; box-shadow: 0 0 0 0 rgba(129, 140, 248, 0); }
        }
      `}</style>

      {/* Top Section: Form + Instruction */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', lgGridTemplateColumns: '1fr 1fr', gap: '30px' }} className="finops-grid">
        {/* Left: Cluster Registration Card */}
        <div className="glass-panel" style={{ padding: '25px', borderRadius: '14px', background: 'rgba(15, 23, 42, 0.4)' }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '1.15rem', color: '#F8FAFC', fontWeight: 'bold' }}>
            ☸️ {t('finops.portal.register_title')}
          </h4>
          <form onSubmit={handleRegisterCluster} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#94A3B8' }}>
                {t('finops.portal.cluster_name')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('finops.portal.cluster_name_placeholder')}
                style={{
                  width: '100%',
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#FFF',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  outline: 'none',
                  fontSize: '0.9rem'
                }}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#94A3B8' }}>
                  {t('finops.portal.cloud_provider')}
                </label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#FFF',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    outline: 'none',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="GCP" style={{ background: '#0F172A' }}>GCP</option>
                  <option value="AWS" style={{ background: '#0F172A' }}>AWS</option>
                  <option value="Azure" style={{ background: '#0F172A' }}>Azure</option>
                  <option value="On-Prem" style={{ background: '#0F172A' }}>On-Prem</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#94A3B8' }}>
                  {t('finops.portal.environment')}
                </label>
                <select
                  value={env}
                  onChange={(e) => setEnv(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#FFF',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    outline: 'none',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="Dev" style={{ background: '#0F172A' }}>Dev</option>
                  <option value="Staging" style={{ background: '#0F172A' }}>Staging</option>
                  <option value="Prod" style={{ background: '#0F172A' }}>Prod</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={registering || !name.trim()}
              className="btn-primary"
              style={{
                marginTop: '10px',
                padding: '11px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: (registering || !name.trim()) ? 'not-allowed' : 'pointer',
                opacity: (registering || !name.trim()) ? 0.6 : 1,
                background: 'var(--primary-color, #006666)',
                color: '#FFF'
              }}
            >
              {registering ? t('finops.portal.registering') : t('finops.portal.btn_register')}
            </button>
          </form>
        </div>

        {/* Right: Dynamic Helm Copy-paste Container */}
        <div className="glass-panel" style={{ padding: '25px', borderRadius: '14px', background: 'rgba(15, 23, 42, 0.4)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#F8FAFC', fontWeight: 'bold' }}>
                💾 {t('finops.portal.helm_snippet')}
              </h4>
              {selectedCluster && rawKeysByCluster[selectedCluster.id] && rawKeysByCluster[selectedCluster.id].length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label htmlFor="key-select" style={{ fontSize: '0.75rem', color: '#94A3B8' }}>🔑 {t('finops.portal.key_select', 'Key:')}</label>
                  <select
                    id="key-select"
                    aria-label="Select Ingestion Key"
                    value={activeRawKey || ''}
                    onChange={(e) => {
                      if (e.target.value === '__generate_new__') {
                        handleQuickGenerateKeyAndPair();
                      } else {
                        setActiveRawKey(e.target.value);
                      }
                    }}
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(99, 102, 241, 0.4)',
                      color: '#34D399',
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                      padding: '3px 8px',
                      borderRadius: '6px',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {rawKeysByCluster[selectedCluster.id].map((k, idx) => (
                      <option key={idx} value={k.rawKey} style={{ background: '#0F172A', color: '#FFF' }}>
                        {k.rawKey.slice(0, 10)}... ({k.name})
                      </option>
                    ))}
                    <option value="__generate_new__" style={{ background: '#0F172A', color: '#818CF8' }}>
                      + {t('finops.portal.btn_quick_generate', 'Generate & Pair New Key')}
                    </option>
                  </select>
                </div>
              ) : selectedCluster && (
                <button
                  type="button"
                  onClick={handleQuickGenerateKeyAndPair}
                  disabled={quickGeneratingKey}
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(79, 70, 229, 0.35))',
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                    color: '#C7D2FE',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: quickGeneratingKey ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.25)'
                  }}
                >
                  <span>⚡</span>
                  {quickGeneratingKey
                    ? t('finops.portal.generating_key', 'Generating key...')
                    : t('finops.portal.btn_one_click_pair', 'One-Click Key Pair')}
                </button>
              )}
            </div>
            <p style={{ margin: '0 0 15px 0', color: '#94A3B8', fontSize: '0.85rem', lineHeight: '1.4' }}>
              {t('finops.portal.helm_subtitle')}
            </p>
            <div style={{
              background: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '8px',
              padding: '12px 16px',
              fontFamily: hasActiveApiKey ? 'monospace' : 'inherit',
              fontSize: '0.82rem',
              color: hasActiveApiKey ? '#34D399' : '#F59E0B',
              wordBreak: hasActiveApiKey ? 'break-all' : 'break-word',
              lineHeight: '1.5',
              maxHeight: '100px',
              overflowY: 'auto'
            }}>
              {hasActiveApiKey ? (
                getHelmCommand()
              ) : (
                `⚠️ ${t('finops.portal.snippet_no_key_warning')}`
              )}
            </div>
          </div>
          <button
            onClick={handleCopyHelmSnippet}
            disabled={!hasActiveApiKey}
            style={{
              width: '100%',
              marginTop: '15px',
              padding: '10px',
              background: !hasActiveApiKey ? 'rgba(255, 255, 255, 0.02)' : (copiedHelm ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)'),
              border: `1px solid ${!hasActiveApiKey ? 'rgba(255, 255, 255, 0.03)' : (copiedHelm ? '#10B981' : 'rgba(255,255,255,0.1)')}`,
              color: !hasActiveApiKey ? '#64748B' : (copiedHelm ? '#A7F3D0' : '#E2E8F0'),
              borderRadius: '8px',
              fontWeight: '600',
              cursor: !hasActiveApiKey ? 'not-allowed' : 'pointer',
              opacity: !hasActiveApiKey ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            {hasActiveApiKey ? (
              copiedHelm ? `✔ ${t('finops.portal.btn_copied')}` : `📋 ${t('finops.portal.btn_copy')}`
            ) : (
              t('finops.portal.snippet_generate_key_first')
            )}
          </button>
        </div>
      </div>

      {/* Onboarding View: Real-Time Telemetry Handshake Status Indicator */}
      {selectedCluster && (
        <div
          data-testid="telemetry-handshake-card"
          className="glass-panel"
          style={{
            padding: '16px 20px',
            borderRadius: '12px',
            background: isClusterActive ? 'rgba(16, 185, 129, 0.08)' : 'rgba(99, 102, 241, 0.08)',
            border: `1px solid ${isClusterActive ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {isClusterActive ? (
              <span
                data-testid="heartbeat-active-icon"
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: '#10B981',
                  boxShadow: '0 0 12px #10B981',
                  display: 'inline-block',
                  flexShrink: 0
                }}
              />
            ) : (
              <span
                data-testid="heartbeat-pulse-icon"
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: '#818CF8',
                  display: 'inline-block',
                  animation: 'subtlePulse 1.8s infinite ease-in-out',
                  flexShrink: 0
                }}
              />
            )}
            <div>
              <div style={{
                color: isClusterActive ? '#34D399' : '#C7D2FE',
                fontWeight: '600',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {isClusterActive ? (
                  <>
                    <span>✔</span>
                    <span>{t('finops.telemetry.handshake_active', 'First heartbeat received! Telemetry active.')}</span>
                  </>
                ) : (
                  <>
                    <span>📡</span>
                    <span>{t('finops.telemetry.handshake_waiting', 'Waiting for first telemetry ping...')}</span>
                  </>
                )}
              </div>
              <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginTop: '3px', lineHeight: '1.4' }}>
                {isClusterActive
                  ? t('finops.telemetry.handshake_active_desc', 'Telemetry stream established. Cluster status is ACTIVE / HEALTHY.')
                  : t('finops.telemetry.handshake_waiting_desc', 'Deploy the Helm snippet above to your Kubernetes cluster. Telemetry handshake triggers automatically on first heartbeat.')}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: 'bold',
              padding: '4px 10px',
              borderRadius: '20px',
              background: isClusterActive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(99, 102, 241, 0.15)',
              color: isClusterActive ? '#34D399' : '#818CF8',
              border: `1px solid ${isClusterActive ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`
            }}>
              {isClusterActive ? 'ACTIVE / HEALTHY' : 'CONNECTING'}
            </span>
            <span style={{
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              color: '#94A3B8',
              background: 'rgba(0,0,0,0.3)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>
              {selectedCluster.name} ({selectedCluster.id.slice(0, 8)}...)
            </span>
          </div>
        </div>
      )}

      {/* Bottom Section: Clusters List & API Keys Table */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', lgGridTemplateColumns: '1.2fr 1fr', gap: '30px' }} className="finops-grid">
        {/* Left: Clusters List */}
        <div className="glass-panel" style={{ padding: '25px', borderRadius: '14px', background: 'rgba(15, 23, 42, 0.4)' }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '1.15rem', color: '#F8FAFC', fontWeight: 'bold' }}>
            ☸️ {t('finops.portal.registered_clusters')}
          </h4>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94A3B8' }}>{t('finops.loading_recommendations')}</div>
          ) : clusters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94A3B8', fontSize: '0.9rem' }}>
              {t('finops.portal.no_clusters')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', textAlign: 'left' }}>
                    <th style={{ padding: '10px 8px' }}>{t('finops.portal.name_col')}</th>
                    <th style={{ padding: '10px 8px' }}>{t('finops.portal.provider_col')}</th>
                    <th style={{ padding: '10px 8px' }}>{t('finops.portal.env_col')}</th>
                    <th style={{ padding: '10px 8px' }}>{t('finops.portal.status_col', 'Status')}</th>
                    <th style={{ padding: '10px 8px' }}>{t('finops.portal.actions_col')}</th>
                  </tr>
                </thead>
                <tbody>
                  {clusters.map((cluster) => {
                    const isSelected = selectedCluster && selectedCluster.id === cluster.id;
                    const status = cluster.status || 'CONNECTING';

                    const renderStatusBadge = () => {
                      switch (status) {
                        case 'ACTIVE':
                          return (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 'bold',
                              background: 'rgba(16, 185, 129, 0.15)',
                              color: '#34D399'
                            }}>
                              {t('finops.telemetry.status.active', 'ACTIVE / HEALTHY')}
                            </span>
                          );
                        case 'DEGRADED':
                          return (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 'bold',
                              background: 'rgba(245, 158, 11, 0.15)',
                              color: '#FBBF24'
                            }}>
                              {t('finops.telemetry.status.degraded', 'DEGRADED / STALE')}
                            </span>
                          );
                        case 'OFFLINE':
                          return (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 'bold',
                              background: 'rgba(156, 163, 175, 0.15)',
                              color: '#9CA3AF'
                            }}>
                              {t('finops.telemetry.status.offline', 'OFFLINE')}
                            </span>
                          );
                        case 'CONNECTING':
                        default:
                          return (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 'bold',
                              background: 'rgba(99, 102, 241, 0.12)',
                              color: '#818CF8',
                              border: '1px dashed rgba(99, 102, 241, 0.4)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: '#818CF8',
                                display: 'inline-block',
                                animation: 'pulse 1.5s infinite'
                              }}></span>
                              {t('finops.telemetry.status.connecting', 'CONNECTING')}
                            </span>
                          );
                      }
                    };

                    return (
                      <tr
                        key={cluster.id}
                        onClick={() => setSelectedCluster(cluster)}
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          color: '#E2E8F0',
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                          transition: 'all 0.15s'
                        }}
                      >
                        <td style={{ padding: '12px 8px', fontWeight: '600' }}>{cluster.name}</td>
                        <td style={{ padding: '12px 8px' }}>{cluster.cloud_provider}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            background: cluster.environment === 'Prod' ? 'rgba(239, 68, 68, 0.15)' : cluster.environment === 'Staging' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                            color: cluster.environment === 'Prod' ? '#FCA5A5' : cluster.environment === 'Staging' ? '#FDE68A' : '#A7F3D0'
                          }}>{cluster.environment}</span>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {renderStatusBadge()}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedCluster(cluster); }}
                            style={{
                              background: 'rgba(255,255,255,0.06)',
                              border: 'none',
                              color: 'var(--primary-color)',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            🔑 {t('finops.portal.manage_keys')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right: API Keys Management for selected cluster */}
        {selectedCluster && (
          <div className="glass-panel" style={{ padding: '25px', borderRadius: '14px', background: 'rgba(15, 23, 42, 0.4)' }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '1.15rem', color: '#F8FAFC', fontWeight: 'bold' }}>
              🔑 {t('finops.portal.active_keys')} "{selectedCluster.name}"
            </h4>
            <p style={{ margin: '0 0 15px 0', color: '#94A3B8', fontSize: '0.8rem', wordBreak: 'break-all' }}>
              ID: {selectedCluster.id}
            </p>

            {/* List existing active keys */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {(!selectedCluster.api_keys || selectedCluster.api_keys.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '15px 0', color: '#94A3B8', fontSize: '0.85rem', fontStyle: 'italic' }}>
                  {t('finops.portal.no_keys')}
                </div>
              ) : (
                selectedCluster.api_keys.map((k) => (
                  <div key={k.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(0,0,0,0.2)',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.04)'
                  }}>
                    <div>
                      <div style={{ color: '#E2E8F0', fontWeight: '600', fontSize: '0.85rem' }}>{k.name}</div>
                      <div style={{ color: '#60A5FA', fontFamily: 'monospace', fontSize: '0.8rem', marginTop: '2px' }}>{k.key_prefix}*********</div>
                    </div>
                    <button
                      onClick={() => handleRevokeKey(k.id)}
                      disabled={revokingKeyId === k.id}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#FCA5A5',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.78rem',
                        fontWeight: 'bold',
                        transition: 'all 0.2s'
                      }}
                    >
                      {revokingKeyId === k.id ? t('finops.portal.revoking') : t('finops.portal.btn_revoke')}
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Generate Key Form */}
            <form onSubmit={handleGenerateKey} style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '15px', display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder={t('finops.portal.key_name_placeholder')}
                style={{
                  flex: 1,
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#FFF',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  outline: 'none',
                  fontSize: '0.85rem'
                }}
                required
              />
              <button
                type="submit"
                disabled={generatingKey || !keyName.trim()}
                style={{
                  background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
                  color: '#FFF',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  cursor: (generatingKey || !keyName.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (generatingKey || !keyName.trim()) ? 0.6 : 1
                }}
              >
                {generatingKey ? t('finops.portal.generating_key') : t('finops.portal.btn_generate_key')}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Key Display Dialog/Modal (Highly secure: shown once) */}
      {showKeyModal && newlyGeneratedKey && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(6px)'
        }}>
          <div className="glass-panel" style={{
            width: '90%',
            maxWidth: '500px',
            padding: '30px',
            background: '#0F172A',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>⚠️</div>
            <h4 style={{ margin: '0 0 10px 0', color: '#F8FAFC', fontSize: '1.25rem', fontWeight: 'bold' }}>
              {t('finops.portal.btn_generate_key')}
            </h4>
            <p style={{ margin: '0 0 20px 0', color: '#EF4444', fontSize: '0.85rem', fontWeight: '600', lineHeight: '1.4' }}>
              {t('finops.portal.key_warning')}
            </p>
            
            <div style={{
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '0.95rem',
              color: '#34D399',
              wordBreak: 'break-all',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{newlyGeneratedKey}</span>
              <button
                onClick={() => copyToClipboard(newlyGeneratedKey, (val) => {})}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  padding: '2px'
                }}
                title={t('finops.portal.btn_copy')}
              >
                📋
              </button>
            </div>

            <button
              onClick={() => { setShowKeyModal(false); }}
              className="btn-primary"
              style={{
                width: '100%',
                padding: '11px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary-color, #006666)',
                color: '#FFF',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Ok, I have saved it
            </button>
          </div>
        </div>
      )}

      {/* Floating Error Toast */}
      {errorMsg && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#EF4444',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '0.9rem',
          fontWeight: '500'
        }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
};

export default GreenOpsPortal;
